import React from 'react';
import {Box, styled} from '@mui/material';
import {
  Icon,
  Body,
  Headline,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {HealthEvaluation} from '@/types/healthEvaluation';

const TRANSLATION_PREFIX =
  'components.social-network-identity-summary.health-scores';

const getRatingIcon = (rating: string) => {
  switch (rating) {
    case 'healthy':
      return 'Navigation-Authorized-Filled';
    case 'investigate':
      return 'Actions-Alert-Line';
    case 'unhealthy':
      return 'Actions-Alert-Line';
    default:
      return 'Navigation-Authorized-Filled';
  }
};

const getRatingAppearance = (rating: string) => {
  switch (rating) {
    case 'healthy':
      return 'brand-secondary';
    case 'investigate':
      return 'neutral';
    case 'unhealthy':
      return 'danger';
    default:
      return 'primary';
  }
};

const getRatingTextColor = (rating: string) => {
  switch (rating) {
    case 'healthy':
      return designSystemToken('semantic.fg.brand-secondary');
    case 'investigate':
      return designSystemToken('semantic.fg.primary');
    case 'unhealthy':
      return designSystemToken('semantic.fg.danger');
    default:
      return designSystemToken('semantic.fg.primary');
  }
};

const getRatingBackgroundColor = (rating: string) => {
  switch (rating) {
    case 'healthy':
      return designSystemToken('comp.button.bg.brand.tinted.hover');
    case 'investigate':
      return designSystemToken('semantic.bg.warning-muted');
    case 'unhealthy':
      return designSystemToken('semantic.bg.danger-secondary');
    default:
      return designSystemToken('semantic.bg.muted');
  }
};

const getScoreIcon = (level: string) => {
  switch (level) {
    case 'positive':
      return '/assets/svg/icon-add-circle-green.svg';
    case 'negative':
      return '/assets/svg/icon-subtract-circle-red.svg';
    case 'neutral':
    default:
      return '/assets/svg/icon-neutral.svg';
  }
};

const Container = styled(Box)<{rating: string}>`
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  background-color: ${({rating}) => getRatingBackgroundColor(rating)};
`;

const IconContainer = styled(Box)`
  display: flex;
  margin-top: 13px;
  margin-left: 16px;
`;

const TextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 14px;
  margin-left: 12px;
  margin-bottom: 14px;
`;

const StyledHeadline = styled(Headline)`
  font-size: 16px;
`;

const StyledBody = styled(Body)`
  font-size: 14px;
`;

const TooltipContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const ScoreContainer = styled(Body)<{scoreLevel: string}>`
  padding: 10px 0 10px 30px;
  text-align: left;
  background-repeat: no-repeat;
  background-position-y: center;
  background-position-x: left;
  background-size: 24px 24px;
  background-image: url('${({scoreLevel}) => getScoreIcon(scoreLevel)}');
`;

function Rating({healthEvaluation}: {healthEvaluation: HealthEvaluation}) {
  const renderTooltip = () => {
    return (
      <TooltipContainer>
        {healthEvaluation.scores.map((score) => (
          <ScoreContainer
            key={score.id}
            size="sm"
            color={designSystemToken('semantic.fg.white')}
            scoreLevel={score.level}
            as="div"
          >
            {translate(`${TRANSLATION_PREFIX}.${score.message}`)}
          </ScoreContainer>
        ))}
      </TooltipContainer>
    );
  };

  return (
    <Tooltip title={renderTooltip()} type="dark" placement="bottom" arrow>
      <Container rating={healthEvaluation.rating}>
        <IconContainer>
          <Icon
            name={getRatingIcon(healthEvaluation.rating)}
            size="medium"
            appearance={getRatingAppearance(healthEvaluation.rating)}
          />
        </IconContainer>
        <TextContainer>
          <StyledHeadline
            size="sm"
            color={getRatingTextColor(healthEvaluation.rating)}
            textAlign="left"
          >
            {translate(`${healthEvaluation.ratingStringKey}`)}
          </StyledHeadline>
          <StyledBody
            size="sm"
            color={getRatingTextColor(healthEvaluation.rating)}
          >
            {translate(`${healthEvaluation.ratingStringKey}-sub`)}
          </StyledBody>
        </TextContainer>
      </Container>
    </Tooltip>
  );
}

export default Rating;
